import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadingContext } from './context/LoadingContext';
import { ToastContext } from './context/ToastContext';
import '../styles/BrandsTagCloudSideBar.css';

const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 28;

const BrandsTagCloudSideBar = () => {
    const [brandList, setBrandList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showHelpMessage, setShowHelpMessage] = useState(false);
    const { sendMessage } = useContext(ToastContext);
    const { startLoading, finishLoading } = useContext(LoadingContext);

    const fetchBrands = useCallback(async () => {
        try {
            startLoading();
            setLoading(true);
            const response = await fetch(new URL('api/products/vendors/index', process.env.APP_URL));
            const data = await response.json();
            setBrandList(data.vendors ?? []);
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            finishLoading();
            setLoading(false);
        }
    }, [startLoading, finishLoading]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const getFontSize = useCallback((count, min, max) => {
        if (max === min) {
            return `${MIN_FONT_SIZE}px`;
        }
        const normalized = (count - min) / (max - min);
        return `${MIN_FONT_SIZE + normalized * (MAX_FONT_SIZE - MIN_FONT_SIZE)}px`;
    }, []);

    const [minCount, maxCount] = useMemo(() => {
        const counts = brandList.map((b) => b.productCount || 0);
        return [Math.min(...counts), Math.max(...counts)];
    }, [brandList]);

    const handleToggleShowHelp = useCallback(() => setShowHelpMessage((prev) => !prev), [setShowHelpMessage]);

    return (
        <div
            className={'brands-tag-cloud--container d-flex flex-column'}>
            <div
                className={'brands-tag-cloud--title'}>
                <h5
                    className={'my-2 mx-3 p-0'}>
                    TAG CLOUD<span
                        aria-label={'Toggle help message'}
                        className={'brands-tag-cloud--help-icon'}>
                        <button
                            style={{
                                all: 'unset'
                            }}
                            onClick={handleToggleShowHelp}><span>[</span> <span>?</span> <span>]</span>
                        </button>
                    </span>
                </h5>
            </div>
            {showHelpMessage && <div
                className={'brands-tag-cloud--help-message'}>
                The <i>brands tag cloud</i> shows all of the brands available for purchase.
                <br />
                <br />
                The bigger the brand appears, the more products it contains.
                <br />
                <br />
                Click on a brand below to see all products listed under that brand.
            </div>}
            {
                loading && <div
                    className={'text-center'}>Loading...</div>
            }
            <div
                className={'d-flex flex-wrap justify-content-center align-items-center gap-1'}>
                {brandList.map((brand, index) => (
                    <div
                        key={`${brand.name}-${index}`}>
                        <Link
                            to={`/brands/${encodeURIComponent(brand.name)}`}
                            style={{
                                fontSize: getFontSize(brand.productCount || 0, minCount, maxCount),
                            }}
                            className={'default-link'}>
                            {brand.name}
                        </Link>
                    </div>
                ))}
            </div>
        </div >
    );
};

export default BrandsTagCloudSideBar;
